// A live voice call with Neo from a web chat (TOM-731, contract §12.20).
//
// Neomaris starts the call (`POST /v1/neo/voice/sessions`), ElevenLabs carries
// the audio (WebRTC, through `@elevenlabs/react`'s client), and Neo answers
// every question in the same conversation the chat types into. What the
// owner sees comes from the call's events stream (§12.20.3): each question as
// heard, then that turn's own chat events, drawn by the chat exactly as a
// typed turn (components/neo/neoStreamTurn.ts). ElevenLabs only speaks.
//
// The call's states: connecting, then listening, thinking (a question is
// being answered) and speaking (from the SDK). Mute stops the microphone; End
// hangs up. Talk is offered only where voice is on for the deployment
// (§12.20.1), the page can use a microphone, and the chat is on a boat.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { readNeoStream, type NeoStreamEvent } from './neoStream';

export type NeoVoicePhase = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking';

/** A call's audio, joined with the token from the start route. */
export interface NeoVoiceCallHandle {
  setMuted(muted: boolean): void;
  end(): Promise<void> | void;
  /** The microphone's level now, 0 to 1, as the call sends it. */
  getInputVolume?(): number;
}

/** Why the audio ended, as the SDK said it: kept for the console, never shown. */
export interface NeoVoiceDisconnect {
  /** `error`, `agent` (the far side ended it) or `user`. */
  reason?: string;
  message?: string;
  closeCode?: number;
  closeReason?: string;
}

export interface NeoVoiceClientCallbacks {
  onConnect(): void;
  onModeChange(mode: 'speaking' | 'listening'): void;
  onDisconnect(details: NeoVoiceDisconnect): void;
  onError(message: string): void;
}

/** How the call's audio joins. */
export interface NeoVoiceStartOptions {
  /**
   * The microphone the browser gave the Talk click (TOM-731). Without it,
   * ElevenLabs' client publishes through LiveKit's defaults, `deviceId:
   * {ideal: 'default'}`: the Mac's system default input, which need not be
   * the microphone the browser chose for the site, and with voice isolation
   * on. With it, the client opens that exact device, with echo cancellation,
   * noise suppression and auto gain, and no voice isolation.
   */
  inputDeviceId?: string;
}

/**
 * What joins the call's audio. The real one is ElevenLabs' client; the e2e
 * suite sets `window.__neomarisVoiceClient` to a fake before the page loads,
 * since a headless browser has no microphone or WebRTC peer to talk to.
 */
export interface NeoVoiceClient {
  start(
    conversationToken: string,
    callbacks: NeoVoiceClientCallbacks,
    options?: NeoVoiceStartOptions
  ): Promise<NeoVoiceCallHandle>;
}

/** Where the call's turns are drawn: the chat's conversation (useNeoConversation). */
export interface NeoVoiceTurns {
  /** The conversation the call continues, or null for a new one. */
  conversationId(): string | null;
  /** A question was heard and accepted: the owner's bubble, and its answer to come. */
  heard(eventId: string, text: string): void;
  /** One of that turn's chat events (§12.3). Without an id, the newest turn's. */
  event(eventId: string | null, event: string, data: NeoStreamEvent): void;
  /** The owner talked over the reply, or another question replaced it. */
  interrupted(eventId: string): void;
  /**
   * The events stream came back after a drop: each question still open is
   * read back by its turn (§12.3.1), since some of its events were missed.
   */
  resync(): void;
  /** The call ended: turns still open stop where they are. */
  ended(): void;
}

export interface NeoVoice {
  /** Talk can be offered here. */
  available: boolean;
  phase: NeoVoicePhase;
  muted: boolean;
  /** Why the call could not start or why it ended, in a sentence; else null. */
  notice: string | null;
  /** The notice offers Try again: the call never reached Neomaris, or dropped. */
  retry: boolean;
  /** During a call: the microphone sends nothing (TOM-731); else null. */
  hint: string | null;
  start(): void;
  end(): void;
  toggleMute(): void;
  dismissNotice(): void;
}

interface VoiceSessionStarted {
  sessionId: string;
  conversationToken: string;
  conversationId: string | null;
  maxSeconds: number;
}

declare global {
  interface Window {
    __neomarisVoiceClient?: NeoVoiceClient;
  }
}

/** ElevenLabs' client, loaded only when a call starts. */
const elevenLabsClient: NeoVoiceClient = {
  async start(conversationToken, callbacks, options) {
    const { Conversation } = await import('@elevenlabs/react');
    // For us, in the console (TOM-731): whether ElevenLabs heard any voice
    // activity, and what kinds of events it sent. Counts and names only.
    const vad = { count: 0, max: 0 };
    const eventTypes: Record<string, number> = {};
    const report = (when: string) =>
      console.info(`neo voice: what ElevenLabs sent (${when})`, {
        vadEvents: vad.count,
        vadMax: Number(vad.max.toFixed(3)),
        eventTypes: { ...eventTypes },
      });
    const call = await Conversation.startSession({
      onVadScore: ({ vadScore }) => {
        vad.count += 1;
        if (Number.isFinite(vadScore)) vad.max = Math.max(vad.max, vadScore);
      },
      onIncomingEvent: (event: unknown) => {
        const type = (event as { type?: unknown } | null)?.type;
        const name = typeof type === 'string' ? type : 'unknown';
        eventTypes[name] = (eventTypes[name] ?? 0) + 1;
      },
      conversationToken,
      connectionType: 'webrtc',
      ...(options?.inputDeviceId === undefined ? {} : { inputDeviceId: options.inputDeviceId }),
      onConnect: () => callbacks.onConnect(),
      onModeChange: ({ mode }) => callbacks.onModeChange(mode),
      // Named fields only, never the SDK's context: it can hold the call's token.
      onDisconnect: (details) => {
        report('at the end');
        callbacks.onDisconnect({
          reason: details.reason,
          ...('message' in details ? { message: String(details.message) } : {}),
          ...('closeCode' in details && details.closeCode !== undefined
            ? { closeCode: details.closeCode }
            : {}),
          ...('closeReason' in details && details.closeReason !== undefined
            ? { closeReason: String(details.closeReason) }
            : {}),
        });
      },
      onError: (message) => callbacks.onError(String(message)),
    });
    setTimeout(() => report('first 15 s'), 15_000);
    return {
      setMuted: (muted) => call.setMicMuted(muted),
      end: () => call.endSession(),
      getInputVolume: () => call.getInputVolume(),
    };
  },
};

function voiceClient(): NeoVoiceClient {
  return (typeof window !== 'undefined' && window.__neomarisVoiceClient) || elevenLabsClient;
}

// Whether voice is on for this deployment, read once per page load.
let statusRead: Promise<boolean> | null = null;
function readVoiceEnabled(): Promise<boolean> {
  statusRead ??= api<{ enabled?: unknown }>('/v1/neo/voice/status').then(
    (body) => body.enabled === true,
    () => {
      statusRead = null;
      return false;
    }
  );
  return statusRead;
}

// The pages showing Talk now, told when voice turns out to be off.
const availabilityListeners = new Set<(on: boolean) => void>();

/** The start route said voice is off (§12.20.2 `voice_disabled`): Talk goes everywhere. */
function markVoiceOff() {
  statusRead = Promise.resolve(false);
  for (const listener of availabilityListeners) listener(false);
}

/**
 * Whether Talk can be offered on a chat about `boatId`: voice on for the
 * deployment (§12.20.1) and a microphone this page can use.
 */
export function useNeoVoiceAvailable(boatId: string | undefined): boolean {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    if (boatId === undefined) {
      setAvailable(false);
      return;
    }
    let live = true;
    const listener = (on: boolean) => {
      if (live) setAvailable(on);
    };
    availabilityListeners.add(listener);
    void Promise.all([readVoiceEnabled(), hasMicrophone()]).then(([on, mic]) => {
      if (live) setAvailable(on && mic);
    });
    return () => {
      live = false;
      availabilityListeners.delete(listener);
    };
  }, [boatId]);
  return available;
}

/** Whether the page can use a microphone at all: a secure page with one plugged in. */
async function hasMicrophone(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !window.isSecureContext) return false;
  const media = navigator.mediaDevices;
  if (media?.getUserMedia === undefined) return false;
  if (window.__neomarisVoiceClient !== undefined) return true;
  try {
    const devices = await media.enumerateDevices();
    return devices.some((d) => d.kind === 'audioinput');
  } catch {
    return false;
  }
}

/** The start route's refusal, as the strip says it (§12.20.2). */
export function voiceStartNotice(status: number | undefined, code: unknown): string {
  if (status === 429) return 'You have used today’s voice minutes. You can still type to Neo.';
  if (status === 403 && code === 'forbidden') return 'Voice is not available on this boat.';
  if (status === 404 && code === 'voice_disabled') return 'Voice is turned off right now.';
  if (status === 503) return 'Voice is not answering right now. Try again in a minute.';
  if (status === 401) return 'Sign in again to talk to Neo.';
  return 'The call could not start. Try again.';
}

/** Why a call ended, when it was not the owner's End (§12.20.3 `voice_ended`). */
export function voiceEndedNotice(reason: unknown): string | null {
  if (reason === 'timeout') return 'The call ended after a quiet minute and a half.';
  if (reason === 'limit') return 'The call reached its time limit.';
  if (reason === 'error') return 'The call dropped. Start it again to keep talking.';
  if (reason === 'ended') return 'The call ended.';
  return null;
}

/**
 * The call never reached Neomaris: ElevenLabs could not reach the voice engine
 * (TOM-731, Mike's first test: a missing Funnel path ended each call after
 * about 7 s with "Speech Engine generation failed" while the strip still said
 * Listening), or the audio failed to join. Never a silent stop.
 */
export const VOICE_COULD_NOT_CONNECT =
  'The call couldn’t connect to Neomaris, so Neo didn’t hear you.';

/**
 * What the strip says when a live call ends without the owner's End: the
 * server's `voice_ended` reason or the SDK's disconnect. A call that ended in
 * an error, or before any question was heard, could not connect and offers
 * Try again; the quiet timeout and the time limit say so; any other end that
 * came after a question is "The call ended."
 */
export function voiceCallEndNotice({
  heard,
  errored,
  serverReason,
}: {
  /** A question of this call was heard (`user_transcript`). */
  heard: boolean;
  /** The SDK reported an error, or disconnected with one. */
  errored: boolean;
  /** `voice_ended`'s reason, when the server ended it. */
  serverReason?: unknown;
}): { notice: string; retry: boolean } {
  if (serverReason === 'timeout' || serverReason === 'limit') {
    return { notice: voiceEndedNotice(serverReason)!, retry: false };
  }
  if (errored || !heard) return { notice: VOICE_COULD_NOT_CONNECT, retry: true };
  if (serverReason === 'error') return { notice: voiceEndedNotice('error')!, retry: true };
  return { notice: 'The call ended.', retry: false };
}

/** What the strip says while the microphone sends nothing (TOM-731). */
export const VOICE_MIC_SILENT =
  'I can’t hear your microphone. Check which microphone your browser is using.';

/** A level at or under this is no sound at all: a working mic in a quiet room sends more. */
const MIC_SILENT_LEVEL = 0.0001;
/** How long a live call's microphone may send nothing before the strip says so. */
export const MIC_SILENT_MS = 6_000;

/**
 * Whether the microphone has sent nothing since the call went live: every
 * level read since then is at zero, for at least MIC_SILENT_MS. Mike's Chrome
 * on his Mac captured nothing while other apps heard his microphone, and the
 * call just sat at Listening (TOM-731).
 */
export function micSilent(levels: readonly number[], liveForMs: number): boolean {
  if (liveForMs < MIC_SILENT_MS || levels.length === 0) return false;
  return levels.every((level) => !(level > MIC_SILENT_LEVEL));
}

/**
 * The device id of the microphone the Talk click got, for the call to open
 * exactly (TOM-731). None for a track without one.
 */
export function clickedMicDeviceId(
  track: { getSettings?: () => { deviceId?: string } } | undefined
): string | undefined {
  const id = track?.getSettings?.().deviceId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/** A voice event's `eventId`, as a string; ElevenLabs numbers them. */
export function voiceEventId(data: unknown): string | null {
  const id = (data as { eventId?: unknown } | null)?.eventId;
  if (typeof id === 'string' && id.length > 0) return id;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  return null;
}

/** The phase the strip shows, from the call and the turns being answered. */
export function neoVoicePhase(
  call: 'idle' | 'connecting' | 'live',
  mode: 'speaking' | 'listening',
  answering: number
): NeoVoicePhase {
  if (call !== 'live') return call;
  if (mode === 'speaking') return 'speaking';
  return answering > 0 ? 'thinking' : 'listening';
}

/** The live call: its id, its audio and its events stream. */
interface LiveVoiceCall {
  sessionId: string | null;
  handle: NeoVoiceCallHandle | null;
  stream: AbortController;
  /** Hung up: late answers from the server or the SDK are ignored. */
  over: boolean;
  /** A question of this call was heard. */
  heard: boolean;
  /** The SDK reported an error during this call. */
  errored: boolean;
  /** Reads the microphone's level once a second while the call is live. */
  micTimer: ReturnType<typeof setInterval> | null;
}

const STREAM_RETRIES = 3;
const STREAM_RETRY_MS = 1_000;
/** A stream open this long was working: its drop starts the count again. */
const STREAM_STEADY_MS = 10_000;

export function useNeoVoice({
  boatId,
  turns,
  answering,
  capabilities,
}: {
  /** The boat the chat is on; there is no voice without one. */
  boatId: string | undefined;
  turns: NeoVoiceTurns;
  /** How many of the call's questions the chat is answering now. */
  answering: number;
  /** The §12.3 capabilities the call's turns offer: the chat's own. */
  capabilities: readonly string[];
}): NeoVoice {
  const enabled = useNeoVoiceAvailable(boatId);
  const [call, setCall] = useState<'idle' | 'connecting' | 'live'>('idle');
  const [mode, setMode] = useState<'speaking' | 'listening'>('listening');
  const [muted, setMuted] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [retry, setRetry] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  const turnsRef = useRef(turns);
  turnsRef.current = turns;
  const capabilitiesRef = useRef(capabilities);
  capabilitiesRef.current = capabilities;
  // Null when idle.
  const callRef = useRef<LiveVoiceCall | null>(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  /** Hangs up: tells the server, stops the audio and the stream. Once per call. */
  const finish = useCallback((why: string | null, tellServer: boolean, offerRetry = false) => {
    const current = callRef.current;
    if (current === null || current.over) return;
    current.over = true;
    callRef.current = null;
    current.stream.abort();
    if (current.micTimer !== null) clearInterval(current.micTimer);
    if (tellServer && current.sessionId !== null) {
      void api(`/v1/neo/voice/sessions/${encodeURIComponent(current.sessionId)}/end`, {
        method: 'POST',
      }).catch(() => undefined);
    }
    try {
      void Promise.resolve(current.handle?.end()).catch(() => undefined);
    } catch {
      // the audio is already gone
    }
    turnsRef.current.ended();
    setCall('idle');
    setMode('listening');
    setMuted(false);
    setNotice(why);
    setRetry(why !== null && offerRetry);
    setHint(null);
  }, []);

  /** A live call ended without the owner's End: say why, never a silent stop. */
  const endedBy = useCallback(
    (current: LiveVoiceCall, serverReason?: unknown) => {
      if (current.over) return;
      const { notice: why, retry: again } = voiceCallEndNotice({
        heard: current.heard,
        errored: current.errored,
        serverReason,
      });
      finish(why, serverReason === undefined, again);
    },
    [finish]
  );

  // Leaving the chat, or moving to another boat, hangs up.
  useEffect(() => () => finish(null, true), [boatId, finish]);

  // Closing or reloading the page hangs up too: a request that outlives the page.
  useEffect(() => {
    function onPageHide() {
      const current = callRef.current;
      if (current === null || current.over || current.sessionId === null) return;
      void fetch(`/v1/neo/voice/sessions/${encodeURIComponent(current.sessionId)}/end`, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => undefined);
    }
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  /** Reads the call's events into the chat until the call ends. */
  const listen = useCallback(
    async (sessionId: string, current: LiveVoiceCall) => {
      const { stream } = current;
      const onEvent = (event: string, data: NeoStreamEvent) => {
        if (stream.signal.aborted) return;
        const t = turnsRef.current;
        switch (event) {
          case 'user_transcript': {
            const id = voiceEventId(data);
            const text = (data as { text?: unknown }).text;
            if (id !== null && typeof text === 'string' && text.trim().length > 0) {
              current.heard = true;
              t.heard(id, text);
            }
            break;
          }
          case 'voice_spoken':
            break;
          case 'voice_interrupted': {
            const id = voiceEventId(data);
            if (id !== null) t.interrupted(id);
            break;
          }
          case 'voice_ended':
            endedBy(current, (data as { reason?: unknown }).reason ?? 'ended');
            return;
          default:
            t.event(voiceEventId(data), event, data);
        }
      };
      // A stream that drops is opened again; one that lived a while resets
      // the count, so a proxy's idle cut never ends a long call.
      let failures = 0;
      let dropped = false;
      while (failures <= STREAM_RETRIES) {
        const opened = Date.now();
        try {
          const res = await fetch(
            `/v1/neo/voice/sessions/${encodeURIComponent(sessionId)}/events`,
            { credentials: 'same-origin', signal: stream.signal }
          );
          if (res.ok && res.body !== null) {
            // Back after a drop: what the open questions missed is read back.
            if (dropped) turnsRef.current.resync();
            await readNeoStream(res.body, onEvent);
          } else if (res.status === 404 || res.status === 401) {
            break;
          }
        } catch {
          // dropped: opened again below while the call is live
        }
        if (stream.signal.aborted) return;
        dropped = true;
        failures = Date.now() - opened > STREAM_STEADY_MS ? 1 : failures + 1;
        await new Promise((resolve) => setTimeout(resolve, STREAM_RETRY_MS));
        if (stream.signal.aborted) return;
      }
      finish('The call lost its connection to Neo. Start it again to keep talking.', true, true);
    },
    [finish, endedBy]
  );

  /**
   * Watches the microphone's level once a second while the call is live: the
   * first 15 s go to the console for us, and a microphone that sends nothing
   * for MIC_SILENT_MS puts a line in the strip. The call keeps running, and
   * the line goes as soon as any sound or a question comes through.
   */
  const watchMic = useCallback((current: LiveVoiceCall, handle: NeoVoiceCallHandle) => {
    if (handle.getInputVolume === undefined) return;
    let since = Date.now();
    let levels: number[] = [];
    let ticks = 0;
    let working = false;
    current.micTimer = setInterval(() => {
      if (current.over) return;
      const level = Number(handle.getInputVolume?.() ?? 0);
      ticks += 1;
      if (ticks <= 15) console.info('neo voice: input level', Number(level.toFixed(4)));
      if (working) {
        if (ticks >= 15 && current.micTimer !== null) clearInterval(current.micTimer);
        return;
      }
      if (current.heard || level > MIC_SILENT_LEVEL) {
        working = true;
        setHint(null);
        return;
      }
      // Muted, the microphone is meant to send nothing.
      if (mutedRef.current) {
        since = Date.now();
        levels = [];
        setHint(null);
        return;
      }
      levels.push(level);
      if (micSilent(levels, Date.now() - since)) setHint(VOICE_MIC_SILENT);
    }, 1_000);
  }, []);

  const start = useCallback(() => {
    if (boatId === undefined || callRef.current !== null) return;
    // The microphone is asked for here, inside the click, before anything else
    // (TOM-731), as ElevenLabs' quickstart does: Safari shows its prompt only
    // in a user gesture. ElevenLabs' client asks again once permitted.
    const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    const micAsked = media?.getUserMedia === undefined ? null : media.getUserMedia({ audio: true });
    const stream = new AbortController();
    const current: LiveVoiceCall = {
      sessionId: null,
      handle: null,
      stream,
      over: false,
      heard: false,
      errored: false,
      micTimer: null,
    };
    callRef.current = current;
    setCall('connecting');
    setNotice(null);
    setRetry(false);
    setHint(null);
    setMuted(false);
    setMode('listening');
    void (async () => {
      // The microphone the click got, which the call then opens exactly.
      let inputDeviceId: string | undefined;
      if (micAsked !== null) {
        try {
          const mic = await micAsked;
          const track = mic.getAudioTracks()[0];
          inputDeviceId = clickedMicDeviceId(track);
          // Which microphone the browser chose, for us in the console.
          console.info(
            'neo voice: microphone',
            track === undefined
              ? { tracks: 0 }
              : {
                  label: track.label,
                  deviceId: inputDeviceId ?? null,
                  readyState: track.readyState,
                  muted: track.muted,
                  enabled: track.enabled,
                }
          );
          for (const t of mic.getTracks()) t.stop();
        } catch (err) {
          const name = err instanceof DOMException ? err.name : 'unknown';
          console.warn('neo voice: the browser did not give the microphone', name);
          finish(
            name === 'NotFoundError'
              ? 'No microphone was found. Choose one in your browser, then try again.'
              : 'Neo needs your microphone to talk. Allow it for this site, then try again.',
            false
          );
          return;
        }
        if (current.over) return;
      }
      let started: VoiceSessionStarted;
      try {
        const conversationId = turnsRef.current.conversationId();
        started = await api<VoiceSessionStarted>('/v1/neo/voice/sessions', {
          method: 'POST',
          body: {
            boatId,
            ...(conversationId === null ? {} : { conversationId }),
            capabilities: [...capabilitiesRef.current],
          },
        });
      } catch (err) {
        const e = err as { status?: number; body?: { error?: unknown } };
        if (e.status === 404 && e.body?.error === 'voice_disabled') markVoiceOff();
        finish(voiceStartNotice(e.status, e.body?.error), false);
        return;
      }
      if (current.over) {
        // Ended while it was starting: the server's call goes too.
        void api(`/v1/neo/voice/sessions/${encodeURIComponent(started.sessionId)}/end`, {
          method: 'POST',
        }).catch(() => undefined);
        return;
      }
      current.sessionId = started.sessionId;
      void listen(started.sessionId, current);
      try {
        const handle = await voiceClient().start(
          started.conversationToken,
          {
            onConnect: () => {
              if (!current.over) setCall('live');
            },
            onModeChange: (next) => {
              if (!current.over) setMode(next);
            },
            onDisconnect: (details) => {
              if (current.over) return;
              // For us, in the console; the owner sees a plain sentence.
              console.warn('neo voice: the call’s audio ended', details);
              if (details.reason === 'error') current.errored = true;
              endedBy(current);
            },
            onError: (message) => {
              console.warn('neo voice: the call’s audio reported an error', message);
              current.errored = true;
            },
          },
          inputDeviceId === undefined ? {} : { inputDeviceId }
        );
        if (current.over) {
          void Promise.resolve(handle.end()).catch(() => undefined);
          return;
        }
        current.handle = handle;
        setCall('live');
        watchMic(current, handle);
      } catch (err) {
        const denied =
          err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'SecurityError');
        if (!denied) console.warn('neo voice: the call’s audio did not join', String(err));
        finish(
          denied
            ? 'Neo needs your microphone to talk. Allow it for this site, then try again.'
            : VOICE_COULD_NOT_CONNECT,
          true,
          !denied
        );
      }
    })();
  }, [boatId, finish, listen, endedBy, watchMic]);

  const end = useCallback(() => finish(null, true), [finish]);

  const toggleMute = useCallback(() => {
    const handle = callRef.current?.handle;
    if (handle === null || handle === undefined) return;
    const next = !mutedRef.current;
    handle.setMuted(next);
    setMuted(next);
  }, []);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return {
    available: enabled,
    phase: neoVoicePhase(call, mode, answering),
    muted,
    notice,
    retry,
    hint,
    start,
    end,
    toggleMute,
    dismissNotice,
  };
}
